'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Button, Typography, type ButtonProps } from '@mui/material';

export type ProCheckoutButtonProps = {
  billing: 'monthly' | 'annual';
  /** Which Stripe price set to use (must match env price IDs and webhook sync). Default `pro`. */
  stripePlan?: 'pro' | 'enterprise' | 'creator' | 'white_label';
  fullWidth?: boolean;
  children?: React.ReactNode;
} & Omit<ButtonProps, 'onClick' | 'disabled'>;

export function ProCheckoutButton({
  billing,
  stripePlan = 'pro',
  fullWidth,
  children,
  color = 'secondary',
  variant = 'contained',
  ...buttonProps
}: ProCheckoutButtonProps) {
  const router = useRouter();
  const { status } = useSession();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleClick = async () => {
    setError(null);
    if (status === 'unauthenticated') {
      router.push(`/auth?callbackUrl=${encodeURIComponent('/pricing')}`);
      return;
    }
    if (status === 'loading') return;
    setLoading(true);
    try {
      const res = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billing, plan: stripePlan }),
      });
      const data = (await res.json().catch(() => ({}))) as { url?: string; message?: string };
      if (!res.ok) {
        setError(data.message || 'Could not start checkout.');
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setError('Checkout did not return a URL.');
    } catch {
      setError('Could not start checkout.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        fullWidth={fullWidth}
        variant={variant}
        color={color}
        disabled={loading || status === 'loading'}
        onClick={() => void handleClick()}
        {...buttonProps}
      >
        {loading ? 'Redirecting…' : children ?? 'Start Pro free trial'}
      </Button>
      {error ? (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }} role="alert">
          {error}
        </Typography>
      ) : null}
    </>
  );
}
